import { AfterViewInit, Component, ElementRef, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { PlIcon } from '@app/icon-library/icons';
import { BarDef, IconColor } from '@shared/models/step.model';
import { IconBorderWidth } from '@app/icon-library/lib/pl-icon.component';
import { ButtonPadding } from '@app/components/button/button.component';

import * as c3 from 'c3';
import { DataPoint, DataSeries, PrimitiveArray } from 'c3';

export function isDataPoint(value: string | DataSeries | DataPoint): value is DataPoint {
  return (
    value.constructor === Object &&
    value.hasOwnProperty('x') &&
    value.hasOwnProperty('value') &&
    value.hasOwnProperty('id') &&
    value.hasOwnProperty('index')
  );
}

@Component({
  selector: 'pl-bar-chart',
  templateUrl: './barChart.component.html',
  styleUrls: ['./barChart.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BarChartComponent implements AfterViewInit {
  @Input() public title: string = '';
  @Input({ required: true }) public barsDef: BarDef[] = [];
  @Input() public barWidth: number = 0.5;

  @ViewChild('chartContainer', { static: true }) public chartContainer!: ElementRef;

  public readonly IconBorderWidth: typeof IconBorderWidth = IconBorderWidth;
  public readonly IconColor: typeof IconColor = IconColor;
  public readonly PlIcon: typeof PlIcon = PlIcon;
  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;

  private readonly X_COLUMN_DEF: string = 'x';

  // Used to generate css class name, if you change this, you have to change css class name selectors in donut.component.scss
  private readonly DATA_COLUMN_DEF: string = 'pl-data';
  private readonly REMAINING_COLUMN_DEF: string = 'pl-remaining';

  public ngAfterViewInit(): void {
    c3.generate({
      bindto: this.chartContainer.nativeElement,
      data: {
        x: this.X_COLUMN_DEF,
        color: this.getColorForColumn.bind(this),
        columns: this.getColumnsDefinition(),
        groups: [[this.REMAINING_COLUMN_DEF, this.DATA_COLUMN_DEF]],
        type: 'bar',
        labels: {
          format: {
            [this.DATA_COLUMN_DEF]: (value: number | { valueOf(): number }): string => {
              return `${value}%`;
            },
          },
        },
        order: null,
      },

      axis: {
        x: {
          type: 'category',
        },
        y: {
          show: false,
        },
      },
      bar: {
        width: {
          ratio: this.barWidth,
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        show: false,
      },
    });
  }

  private getColumnsDefinition(): [string, ...PrimitiveArray][] {
    const axisLabelDef: [string, ...string[]] = [this.X_COLUMN_DEF];
    const dataDef: [string, ...number[]] = [this.DATA_COLUMN_DEF];
    const remainingDef: [string, ...number[]] = [this.REMAINING_COLUMN_DEF];
    for (const barDef of this.barsDef) {
      axisLabelDef.push(barDef.title);
      dataDef.push(+barDef.value);
      remainingDef.push(100 - +barDef.value);
    }

    return [axisLabelDef, dataDef, remainingDef];
  }

  private getColorForColumn(color: string, d: string | DataSeries | DataPoint): string {
    if (isDataPoint(d) && d.id === this.DATA_COLUMN_DEF) {
      let colorFromBarDef: string = this.barsDef[d.index].color;
      if (!colorFromBarDef.startsWith('#')) {
        colorFromBarDef = `#${colorFromBarDef}`;
      }

      if (this.isValidHexaCode(colorFromBarDef)) {
        return colorFromBarDef;
      }
    }

    return color;
  }

  private isValidHexaCode(color: string): boolean {
    const regex: RegExp = new RegExp(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/);
    return regex.test(color);
  }
}
